import React from "react";
import { useEffect, useState } from "react";
import { Definition } from "../../../../../data/Definition";
import { getProperty } from "../../../utils/dwHelpers";
import { useUnitConversion, formatUnitAbbreviation} from '../../../utils/unitConversion';
import { dcRound } from '../../../utils/misc';
import { useStore } from '../../../providers/AppProvider';
import { l2BBConfig } from '../../../utils/settings';
import _ from "lodash";

//import { StatValue } from "../../types/types";

type StatsProps = {
    definition: Definition | null,
    metric: string
    description: string,
    loading: boolean,
    dataSoilStats: any | null,
    dataSoilStats2025: any | null,
    dataSoilDRLStats: any | null,
    propertyUnitsData: any | null,
    setDataSoilStats: Function,
    bbCardByCategory: any | null,
}

const AboveWidgetSoilFertilityCell = ({ 
    definition,
    metric, 
    description, 
    loading, 
    dataSoilStats, 
    dataSoilStats2025,
    dataSoilDRLStats,
    propertyUnitsData,
    setDataSoilStats,
    bbCardByCategory,
}: StatsProps) => {
    //const showChange = !loading && data ? data.change != undefined : false;
    const formatStat = ( v: number | null | undefined, propKey: string) => {
        return (
            <span>
            {v !== null && v !== undefined ? v.toLocaleString(undefined, {
                minimumFractionDigits: 0,
                maximumFractionDigits: 2
                })
            : <i>n/a</i>}<br></br>
            {getPropertyUnit(propertyUnitsData, definition, propKey)}
            </span>
         )
    }

    const { localePreferences } = useStore();
    const { convertToPreferredUnitValue, convertToPreferredUnitString, unitsConvert } = useUnitConversion();
    const [dataAssessed, setDataAssessed] = useState<boolean>(false);
    const [preferredLenAbbr, setPreferredLenAbbr] = useState<string>("");
    const [preferredAreaAbbr, setPreferredAreaAbbr] = useState<string>("ac");
    const [columnsOn2025, setColumnsOn2025] = useState<any>(() => {
        const ddProps = new Set<string>();

        const walk = (node: any) => {
            if (node == null) return;
            if (Array.isArray(node)) {
                node.forEach(walk);
                return;
            }
            if (typeof node === 'object') {
                // collect ddProp if present
                if ('ddProp' in node && node.ddProp != null) {
                    const v = (node as any).ddProp;
                    if (Array.isArray(v)) {
                        v.forEach((p) => typeof p === 'string' && ddProps.add(p));
                    } else if (typeof v === 'string') {
                        ddProps.add(v);
                    }
                }
                // continue walking
                Object.values(node).forEach(walk);
            }
        };

        walk(l2BBConfig);

        const initial: any = {};
        ddProps.forEach((p) => {
            //const key = `${String(p).trim().replace(/\s+/g, '_')}_mean`; // replaces " " w/ "_" --> but no need  
            const key = `${String(p).trim()}_mean`;
            initial[key] = false;
        });

        return initial;
    });

    const [columnsOn, setColumnsOn] = useState<any>({
        Nitrogen_mean : false, // Nitrogen_mean
        Phosphorus_mean : false, // Phosphorus_mean
        Potassium_mean : false, // Potassium_mean
        Calcium_mean : false, // Calcium_mean
        Magnesium_mean: false, // Magnesium_mean
        Sulfur_mean : false, // ["Sulfur_mean"]
        Organic_Matter_mean : false, // ["Organic Matter_mean"]
        BD_mean : false, // ["BD_mean"]
        pH_mean : false, // pH_mean
        // 
        CEC_mean : false, // CEC_mean
        Potassium_Saturation_mean : false, // ["Potassium Saturation_mean"]
        Magnesium_Saturation_mean : false, // ["Magnesium Saturation_mean"]
        Calcium_Saturation_mean : false, // ["Calcium Saturation_mean"]
        Sodium_Saturation_mean : false, // ["Sodium Saturation_mean"]
        Hydrogen_Saturation_mean : false, // ["Hydrogen Saturation_mean"]
        //
        Zinc_mean : false, // ["Zinc_mean"]
        Iron_mean : false, // ["Iron_mean"]
        Manganese_mean : false, // ["Manganese_mean"]
        Boron_mean : false, // ["Boron_mean"]
        
        //Soil Fertility
        //--> Sulfur == Soluble Sulfur == Soluble Sulfur_mean > 8.2211
        
        // Soil Micronutrients
        // Cu == Copper == Copper_mean > 2.3832
        //--> Boron == Soluble Boron == Soluble Boron_mean > 0.1597

        Copper_mean: false,
        // Soil Cation Ratios
        // BS == Base Saturation == Base Saturation_mean > 100
        // EA == Exchangeable Acidity == Exchangeable Acidity_mean > 0
        //--> Ca% == Exchangeable Calcium Percentage == Exchangeable Calcium Percentage_mean > 52.2501
        //--> Mg% == Exchangeable Magnesium Percentage == Exchangeable Magnesium Percentage_mean > 38.1291
        //--> K% == Exchangeable Potassium Percentage == Exchangeable Potassium Percentage_mean > 0.3898
        //--> Na% == Exchangeable Sodium Percentage == Exchangeable Sodium Percentage_mean > 9.231
        Base_Saturation_mean: false,
        Exchangeable_Acidity_mean: false,
        Exchangeable_Calcium_Percentage_mean: false,
        Exchangeable_Magnesium_Percentage_mean: false,
        Exchangeable_Potassium_Percentage_mean: false,
        Exchangeable_Sodium_Percentage_mean: false,

        // Soil Paste
        // SP == Saturation Percentage == Saturation Percentage_mean > 36.2622
        // EC == Electrical Conductivity of Saturated Paste Extract == Electrical Conductivity of Saturated Paste Extract_mean > 0.2377
        // Ca - sol == Soluble Calcium == Soluble Calcium_mean > 0.3065
        // Mg - sol == Soluble Magnesium == Soluble Magnesium_mean > 0.0006
        // K - sol == Soluble Potassium == Soluble Potassium_mean > 0.2519
        // Na - sol == Soluble Sodium == Soluble Sodium_mean > 0.0031
        // Salts == Soluble Salts == Soluble Salts_mean > 0.5621
        Saturation_Percentage_mean: false,
        Electrical_Conductivity_of_Saturated_Paste_Extract_mean: false,
        Soluble_Calcium_mean: false,
        Soluble_Magnesium_mean: false,
        Soluble_Potassium_mean: false,
        Soluble_Sodium_mean: false,
        Soluble_Salts_mean: false,
 
        //Soil Paste Ratios
        // X Sol Ca - pct == NG??? Soluble Calcium | Soluble Calcium Magnesium Ratio
        // X Sol Mg - pct == NG??? Soluble Magnesium
        // X Sol K - pct == NG??? Soluble Potassium | Soluble Potassium Magnesium Ratio
        // Sol Na - pct == Soluble Sodium Percentage == Soluble Sodium Percentage_mean > 13.7681
        // SAR == Sodium Adsorption Ratio == Sodium Adsorption Ratio_mean > 0.5133
        Soluble_Sodium_Percentage_mean: false,
        Sodium_Adsorption_Ratio_mean: false,
        Ca_PCT_mean: false,
        Mg_PCT_mean: false,
        K_PCT_mean: false,

        //Soil Health
        // X OC == NG - "Organic Carbon" or Soil Organic Carbom" we dont have that ???
        // Macro Porosity == Macroporosity == Macroporosity_mean > 30.9344
        // Pore Connectivity == Macropore Connectivity (NG - "I think so") == Macropore Connectivity_mean > 16.7011
        Macroporosity_mean: false,
        Macropore_Connectivity_mean: false,
        
    });  
    
    useEffect(() => {
        console.log("C081925 localePreferences", localePreferences)
        const _preferredAreaMid = _.get(localePreferences, 'measureUnits.area.mid');
        const _preferredAreaAbbr: string =
            typeof _preferredAreaMid === 'string' ? _preferredAreaMid : _preferredAreaMid?.abbr || 'ac';

        // [LEN] preferred length unit abbr (for spacing label/input + submission)
        const _preferredLenSmall = _.get(localePreferences, 'measureUnits.length.small');
        const _preferredLenAbbr: string =
            typeof _preferredLenSmall === 'string' ? _preferredLenSmall : _preferredLenSmall?.abbr || 'in';
        setPreferredLenAbbr(_preferredLenAbbr);
        setPreferredAreaAbbr(_preferredAreaAbbr);
    }, [localePreferences]);

    useEffect(() => {
        console.log("D082225 dataSoilStats", dataSoilStats);
        console.log("D082225 dataSoilStats2025", dataSoilStats2025);
        console.log("D082225 metric", metric);
        console.log("D082225 bbCardByCategory", bbCardByCategory);
        console.log("D082225 columnsOn2025", columnsOn2025)
        if (dataSoilStats && dataSoilStats2025){
            assessColumnsForNA();
        } 
    }, [dataSoilStats, dataSoilStats2025]);

    useEffect(() => {
        console.log("B092425 definition", definition);
        console.log("B092425 metric", metric);
        console.log("B092425 description", description);
        console.log("B092425 loading", loading);
        console.log("B092425 dataSoilStats", dataSoilStats);
        console.log("B092425 dataSoilStats2025", dataSoilStats2025);
        console.log("B092425 dataSoilDRLStats", dataSoilDRLStats);
        console.log("B092425 propertyUnitsData", propertyUnitsData);
        console.log("B092425 setDataSoilStats", setDataSoilStats);
        console.log("B092425 bbCardByCategory", bbCardByCategory);
    }, [definition,
        metric, 
        description, 
        loading, 
        dataSoilStats, 
        dataSoilStats2025,
        dataSoilDRLStats,
        propertyUnitsData,
        setDataSoilStats,
        bbCardByCategory]);
    //[`${getProperty(definition, ['...'])?.property}_mean`]
    
    const assessColumnsForNA = () => {
        // Clone state before mutating to ensure React change detection
        let tempColumnsOn = { ...columnsOn };
        let tempColumnsOn2025 = { ...columnsOn2025 };
        /*let _dataSoilStats = dataSoilStats.map((s:any) => {
            const caSol = s[`${getProperty(definition, ['Soluble Calcium'])?.property}_mean`] || 0;
            const mgSol = s[`${getProperty(definition, ['Soluble Magnesium'])?.property}_mean`] || 0;
            const kSol = s[`${getProperty(definition, ['Soluble Potassium'])?.property}_mean`] || 0;
            const naSol = s[`${getProperty(definition, ['Soluble Sodium'])?.property}_mean`] || 0;

            return {
                ...s,
                Ca_PCT_mean: caSol / (caSol + mgSol + kSol + naSol),
                Mg_PCT_mean: mgSol / (caSol + mgSol + kSol + naSol),
                K_PCT_mean: kSol / (caSol + mgSol + kSol + naSol)
            };
        });*/
        // First, dynamically assess 2025 availability using l2BBConfig-derived keys
        if (Array.isArray(dataSoilStats2025) && dataSoilStats2025.length && tempColumnsOn2025) {
            const keys2025 = Object.keys(tempColumnsOn2025);
            dataSoilStats2025.forEach((row: any) => {
                keys2025.forEach((k) => {
                    // Map underscored key back to data key, preserving the "_mean" suffix underscore
                    let sKey: string;
                    if (k.endsWith('_mean')) {
                        const base = k.slice(0, -5).replace(/_/g, ' ');
                        sKey = `${base}_mean`;
                    } else {
                        sKey = k.replace(/_/g, ' ');
                    }
                    const val = row?.[sKey];
                    if (val !== undefined && val !== null) {
                        tempColumnsOn2025[k] = true;
                    }
                });
            });
            console.log("A082225 tempColumnsOn2025", tempColumnsOn2025);
            setColumnsOn2025({ ...tempColumnsOn2025 });
        }

        // Then assess current data set as before
        dataSoilStats.forEach((s:any, i:number) => {
            console.log("B030823 s", s); 
            const caSol = s[`${getProperty(definition, ['Soluble Calcium'])?.property}_mean`] || false;
            const mgSol = s[`${getProperty(definition, ['Soluble Magnesium'])?.property}_mean`] || false;
            const kSol = s[`${getProperty(definition, ['Soluble Potassium'])?.property}_mean`] || false;
            const naSol = s[`${getProperty(definition, ['Soluble Sodium'])?.property}_mean`] || false;
            if (caSol && mgSol && kSol && naSol){
                tempColumnsOn.Ca_PCT_mean = true;
                tempColumnsOn.Mg_PCT_mean = true;
                tempColumnsOn.K_PCT_mean = true;
            }

            tempColumnsOn.Boron_mean = s[`${getProperty(definition, ['boron'])?.property}_mean`] !== undefined ? true : s[`${getProperty(definition, ['boron'])?.property}_mean`];
            tempColumnsOn.CEC_mean = s[`${getProperty(definition, ['cec'])?.property}_mean`] !== undefined ? true : s[`${getProperty(definition, ['cec'])?.property}_mean`];
            tempColumnsOn.Calcium_Saturation_mean = s[`${getProperty(definition, ['calcium saturation'])?.property}_mean`] !== undefined ? true : s[`${getProperty(definition, ['calcium saturation'])?.property}_mean`];
            tempColumnsOn.Calcium_mean = s[`${getProperty(definition, ['calcium'])?.property}_mean`] !== undefined ? true : s[`${getProperty(definition, ['calcium'])?.property}_mean`];
            tempColumnsOn.Hydrogen_Saturation_mean = s[`${getProperty(definition, ['hydrogen saturation'])?.property}_mean`] !== undefined ? true : s[`${getProperty(definition, ['hydrogen saturation'])?.property}_mean`];
            tempColumnsOn.Iron_mean = s[`${getProperty(definition, ['iron'])?.property}_mean`] !== undefined ? true : s[`${getProperty(definition, ['iron'])?.property}_mean`];
            tempColumnsOn.Magnesium_mean = s[`${getProperty(definition, ['magnesium'])?.property}_mean`] !== undefined ? true : s[`${getProperty(definition, ['magnesium'])?.property}_mean`];
            tempColumnsOn.Manganese_mean = s[`${getProperty(definition, ['manganese'])?.property}_mean`] !== undefined ? true : s[`${getProperty(definition, ['manganese'])?.property}_mean`];
            tempColumnsOn.Nitrogen_mean = s[`${getProperty(definition, ['nitrogen'])?.property}_mean`] !== undefined ? true : s[`${getProperty(definition, ['nitrogen'])?.property}_mean`];
            tempColumnsOn.Organic_Matter_mean = s[`${getProperty(definition, ['organic matter'])?.property}_mean`] !== undefined ? true : s[`${getProperty(definition, ['organic matter'])?.property}_mean`] ;
            tempColumnsOn.BD_mean = s[`${getProperty(definition, ['bd'])?.property}_mean`] !== undefined ? true : s[`${getProperty(definition, ['bd'])?.property}_mean`] ;
            tempColumnsOn.Phosphorus_mean = s[`${getProperty(definition, ['phosphorus'])?.property}_mean`]  !== undefined ? true : s[`${getProperty(definition, ['phosphorus'])?.property}_mean`];
            tempColumnsOn.Potassium_Saturation_mean = s[`${getProperty(definition, ['potassium saturation'])?.property}_mean`] !== undefined ? true : s[`${getProperty(definition, ['potassium saturation'])?.property}_mean`];
            tempColumnsOn.Magnesium_Saturation_mean = s[`${getProperty(definition, ['magnesium saturation'])?.property}_mean`] !== undefined ? true : s[`${getProperty(definition, ['magnesium saturation'])?.property}_mean`];
            tempColumnsOn.Potassium_mean = s[`${getProperty(definition, ['potassium'])?.property}_mean`] !== undefined ? true : s[`${getProperty(definition, ['potassium'])?.property}_mean`];
            tempColumnsOn.Sodium_Saturation_mean = s[`${getProperty(definition, ['sodium saturation'])?.property}_mean`] !== undefined ? true : s[`${getProperty(definition, ['sodium saturation'])?.property}_mean`];
            tempColumnsOn.Sulfur_mean = s[`${getProperty(definition, ['sulfur'])?.property}_mean`] !== undefined ? true : s[`${getProperty(definition, ['sulfur'])?.property}_mean`];
            tempColumnsOn.Zinc_mean = s[`${getProperty(definition, ['zinc'])?.property}_mean`] !== undefined ? true : s[`${getProperty(definition, ['zinc'])?.property}_mean`];
            tempColumnsOn.pH_mean = s[`${getProperty(definition, ['ph'])?.property}_mean`] !== undefined ? true : s[`${getProperty(definition, ['ph'])?.property}_mean`];


            //Soil Fertility
            //--> Sulfur == Soluble Sulfur == Soluble Sulfur_mean > 8.2211
            tempColumnsOn.Soluble_Sulfur_mean = s[`${getProperty(definition, ['Soluble Sulfur'])?.property}_mean`] !== undefined ? true : s[`${getProperty(definition, ['Soluble Sulfur'])?.property}_mean`];

            // Soil Micronutrients
            // Cu == Copper == Copper_mean > 2.3832            
            //--> Boron == Soluble Boron == Soluble Boron_mean > 0.1597
            tempColumnsOn.Soluble_Boron_mean = s[`${getProperty(definition, ['Soluble Boron'])?.property}_mean`] !== undefined ? true : s[`${getProperty(definition, ['Soluble Boron'])?.property}_mean`];
            tempColumnsOn.Copper_mean = s[`${getProperty(definition, ['Copper'])?.property}_mean`] !== undefined ? true : s[`${getProperty(definition, ['Copper'])?.property}_mean`];


            // Soil Cation Ratios
            // BS == Base Saturation == Base Saturation_mean > 100
            // EA == Exchangeable Acidity == Exchangeable Acidity_mean > 0
            //--> Ca% == Exchangeable Calcium Percentage == Exchangeable Calcium Percentage_mean > 52.2501
            //--> Mg% == Exchangeable Magnesium Percentage == Exchangeable Magnesium Percentage_mean > 38.1291
            //--> K% == Exchangeable Potassium Percentage == Exchangeable Potassium Percentage_mean > 0.3898
            //--> Na% == Exchangeable Sodium Percentage == Exchangeable Sodium Percentage_mean > 9.231
            tempColumnsOn.Base_Saturation_mean = s[`${getProperty(definition, ['Base Saturation'])?.property}_mean`] !== undefined ? true : s[`${getProperty(definition, ['Base Saturation'])?.property}_mean`];
            console.log("112424 getProperty(definition, ['Exchangeable Acidity'])", getProperty(definition, ['Exchangeable Acidity']));
            console.log("112424 s", s);
                //`] !== undefined ? true : s[`${getProperty(definition, ['Exchangeable Acidity'])?.property}_mean`];
            tempColumnsOn.Exchangeable_Acidity_mean = s[`${getProperty(definition, ['Exchangeable Acidity'])?.property}_mean`] !== undefined ? true : s[`${getProperty(definition, ['Exchangeable Acidity'])?.property}_mean`];
            console.log("112424 tempColumnsOn.Exchangeable_Acidity_mean", tempColumnsOn.Exchangeable_Acidity_mean);
            tempColumnsOn.Exchangeable_Calcium_Percentage_mean = s[`${getProperty(definition, ['Exchangeable Calcium Percentage'])?.property}_mean`] !== undefined ? true : s[`${getProperty(definition, ['Exchangeable Calcium Percentage'])?.property}_mean`];
            tempColumnsOn.Exchangeable_Magnesium_Percentage_mean = s[`${getProperty(definition, ['Exchangeable Magnesium Percentage'])?.property}_mean`] !== undefined ? true : s[`${getProperty(definition, ['Exchangeable Magnesium Percentage'])?.property}_mean`];
            tempColumnsOn.Exchangeable_Potassium_Percentage_mean = s[`${getProperty(definition, ['Exchangeable Potassium Percentage'])?.property}_mean`] !== undefined ? true : s[`${getProperty(definition, ['Exchangeable Potassium Percentage'])?.property}_mean`];
            tempColumnsOn.Exchangeable_Sodium_Percentage_mean = s[`${getProperty(definition, ['Exchangeable Sodium Percentage'])?.property}_mean`] !== undefined ? true : s[`${getProperty(definition, ['Exchangeable Sodium Percentage'])?.property}_mean`];
            tempColumnsOn.Exchangeable_Calcium_Percentage_mean = s[`${getProperty(definition, ['Exchangeable Calcium Percentage'])?.property}_mean`] !== undefined ? true : s[`${getProperty(definition, ['Exchangeable Calcium Percentage'])?.property}_mean`];
            tempColumnsOn.Exchangeable_Magnesium_Percentage_mean = s[`${getProperty(definition, ['Exchangeable Magnesium Percentage'])?.property}_mean`] !== undefined ? true : s[`${getProperty(definition, ['Exchangeable Magnesium Percentage'])?.property}_mean`];
            tempColumnsOn.Exchangeable_Potassium_Percentage_mean = s[`${getProperty(definition, ['Exchangeable Potassium Percentage'])?.property}_mean`] !== undefined ? true : s[`${getProperty(definition, ['Exchangeable Potassium Percentage'])?.property}_mean`];
            tempColumnsOn.Exchangeable_Potassium_Percentage_mean = s[`${getProperty(definition, ['Exchangeable Sodium Percentage'])?.property}_mean`] !== undefined ? true : s[`${getProperty(definition, ['Exchangeable Sodium Percentage'])?.property}_mean`];

            // Soil Paste
            // SP == Saturation Percentage == Saturation Percentage_mean > 36.2622
            // EC == Electrical Conductivity of Saturated Paste Extract == Electrical Conductivity of Saturated Paste Extract_mean > 0.2377
            // Ca - sol == Soluble Calcium == Soluble Calcium_mean > 0.3065
            // Mg - sol == Soluble Magnesium == Soluble Magnesium_mean > 0.0006
            // K - sol == Soluble Potassium == Soluble Potassium_mean > 0.2519
            // Na - sol == Soluble Sodium == Soluble Sodium_mean > 0.0031
            // Salts == Soluble Salts == Soluble Salts_mean > 0.5621
            tempColumnsOn.Saturation_Percentage_mean = s[`${getProperty(definition, ['Saturation Percentage'])?.property}_mean`] !== undefined ? true : s[`${getProperty(definition, ['Saturation Percentage'])?.property}_mean`];
            tempColumnsOn.Electrical_Conductivity_of_Saturated_Paste_Extract_mean = s[`${getProperty(definition, ['Electrical Conductivity of Saturated Paste Extract'])?.property}_mean`] !== undefined ? true : s[`${getProperty(definition, ['Electrical Conductivity of Saturated Paste Extract'])?.property}_mean`];
            tempColumnsOn.Soluble_Calcium_mean = s[`${getProperty(definition, ['Soluble Calcium'])?.property}_mean`] !== undefined ? true : s[`${getProperty(definition, ['Soluble Calcium'])?.property}_mean`];
            tempColumnsOn.Soluble_Magnesium_mean = s[`${getProperty(definition, ['Soluble Magnesium'])?.property}_mean`] !== undefined ? true : s[`${getProperty(definition, ['Soluble Magnesium'])?.property}_mean`];
            tempColumnsOn.Soluble_Potassium_mean = s[`${getProperty(definition, ['Soluble Potassium'])?.property}_mean`] !== undefined ? true : s[`${getProperty(definition, ['Soluble Potassium'])?.property}_mean`];
            tempColumnsOn.Soluble_Sodium_mean = s[`${getProperty(definition, ['Soluble Sodium'])?.property}_mean`] !== undefined ? true : s[`${getProperty(definition, ['Soluble Sodium'])?.property}_mean`];
            tempColumnsOn.Soluble_Salts_mean = s[`${getProperty(definition, ['Soluble Salts'])?.property}_mean`] !== undefined ? true : s[`${getProperty(definition, ['Soluble Salts'])?.property}_mean`];

            //Soil Paste Ratios
            // X Sol Ca - pct == NG??? Soluble Calcium | Soluble Calcium Magnesium Ratio
            // X Sol Mg - pct == NG??? Soluble Magnesium
            // X Sol K - pct == NG??? Soluble Potassium | Soluble Potassium Magnesium Ratio
            // Sol Na - pct == Soluble Sodium Percentage == Soluble Sodium Percentage_mean > 13.7681
            // SAR == Sodium Adsorption Ratio == Sodium Adsorption Ratio_mean > 0.5133
            tempColumnsOn.Soluble_Sodium_Percentage_mean = s[`${getProperty(definition, ['Soluble Sodium Percentage'])?.property}_mean`] !== undefined ? true : s[`${getProperty(definition, ['Soluble Sodium Percentage'])?.property}_mean`];
            tempColumnsOn.Sodium_Adsorption_Ratio_mean = s[`${getProperty(definition, ['Sodium Adsorption Ratio'])?.property}_mean`] !== undefined ? true : s[`${getProperty(definition, ['Sodium Adsorption Ratio'])?.property}_mean`];
            //Soil Health
            // X OC == NG - "Organic Carbon" or Soil Organic Carbom" we dont have that ???
            // Macro Porosity == Macroporosity == Macroporosity_mean > 30.9344
            // Pore Connectivity == Macropore Connectivity (NG - "I think so") == Macropore Connectivity_mean > 16.7011
            tempColumnsOn.Macroporosity_mean = s[`${getProperty(definition, ['Macroporosity'])?.property}_mean`] !== undefined ? true : s[`${getProperty(definition, ['Macroporosity'])?.property}_mean`];
            tempColumnsOn.Macropore_Connectivity_mean = s[`${getProperty(definition, ['Macropore Connectivity'])?.property}_mean`] !== undefined ? true : s[`${getProperty(definition, ['Macropore Connectivity'])?.property}_mean`];
     /*   */
        })
        setColumnsOn({ ...tempColumnsOn });
        setDataAssessed(true);
    }

    const getPropertyUnit = (propertyUnitsData: any, definitions: Definition | null, propertyKey: string) => {
        console.log("081523 propertyUnitsData", propertyUnitsData);
        console.log("081523 definitions", definitions);
        console.log("081523 propertyKey", propertyKey);
        let definition = definitions?.property.filter((f: any) => {
            return f.property.toLowerCase() === propertyKey.toLowerCase();
        })[0];
        console.log("A081523 definition", definition);
        let propUnits = propertyUnitsData.syncarto_dw_property.filter((f: any) => {
            return f.id === definition?.id;
        });
        console.log("A081523 propUnits", propUnits);

        if (propUnits?.length){
            return propUnits[0].unit.abbrv;
        }else{
            console.log("B081523 propetyKey", propertyKey);
            console.log("B081523 propUnits", propUnits);
            return ""
        }
    }
    
    const depthLabels = [
        ["SL",1.43],
        ["SCIL",1.43],
        ["SCI",1.43],
        ["L",1.43],
    ]
    return (
        <div>
            <dd>
                { loading && <div className="text-muted text-sm mt-2">Loading...</div> }

                { /*console.log("VET BB CARD dataSoilStats", dataSoilStats) }
                { console.log("VET BB CARD dataSoilDRLStats", dataSoilDRLStats) }
                { console.log("VET BB CARD metric", metric) */}

                { !loading && !dataSoilStats || (dataSoilStats?.length <= 0) && <div className="text-muted text-sm mt-2">Data unavailable for this date.</div> }    

                { !loading && dataSoilStats && (dataSoilStats.length > 0) && dataSoilDRLStats && dataAssessed &&
                <>
                <>
                {(() => {
                    type SoilStatsTableProps = {
                        metric: string;
                        dataSoilStats: any[];
                        dataSoilDRLStats: any[];
                        columnsOn: any;
                        definition: Definition | null;
                        preferredLenAbbr: string;
                        formatStat: (v: number | null | undefined, propKey: string) => React.ReactNode;
                    };
/*
create a similar FC here called SoilStatsTable2025, this should appear on screen similar to the existing FC, but it should be dynamic (not hardcoded html elements but instead a function to create them/iterate the config).

Use the bbCardByCategory, columnsOn2025, and dataSoilStats2025 vars to construct this. 

1) bbCardByCategory[metric] will indicate which variables should show up in this FC per the 2nd level key value pairs (all other categories should be excluded). The 2nd level key will be the same as the keys in columnsOn2025, except that all keys in columnsOn2025 have a "_mean" suffix. So you will need to add that suffix to the 2nd level keys in bbCardByCategory[metric] to match them up to columnsOn2025.
2) columnsOn2025 will indicate if the variable has any data at all (true/false). If false, exclude from display like the existing FC does. As noted, the keys in columnsOn2025 have a "_mean" suffix.
3) dataSoilStats2025 is the data to use, similar to dataSoilStats in the existing FC is an array of objects with the same structure as dataSoilStats. Note that the keys inside each object in this array also have a "_mean" suffix, so you will need to add that suffix to the 2nd level keys in bbCardByCategory[metric] to match them up to the keys in each object in dataSoilStats2025.

*/
                    type SoilStatsTable2025Props = {
                        metric: string;
                        dataSoilStats2025: any[];
                        dataSoilDRLStats: any[];
                        columnsOn2025: Record<string, boolean>;
                        definition: Definition | null;
                        preferredLenAbbr: string;
                        formatStat: (v: number | null | undefined, propKey: string) => React.ReactNode;
                        bbCardByCategory: any;
                    };

                    const SoilStatsTable2025: React.FC<SoilStatsTable2025Props> = ({
                        metric,
                        dataSoilStats2025,
                        dataSoilDRLStats,
                        columnsOn2025,
                        definition,
                        preferredLenAbbr,
                        formatStat,
                        bbCardByCategory,
                    }) => {
                        // Normalize a "_mean" key for looking up in row objects (convert underscores in the base part to spaces)
                        const toRowKey = (k: string) => {
                            if (!k) return k;
                            if (k.endsWith("_mean")) {
                                const base = k.slice(0, -5).replace(/_/g, " ");
                                return `${base}_mean`;
                            }
                            return k.replace(/_/g, " ");
                        };

                        const collectRequestedKeys = (): string[] => {
                            const raw = bbCardByCategory?.[metric];
                            console.log("E082225 raw", raw);
                            console.log("E082225 metric", metric);

                            if (!raw || typeof raw !== "object" || Array.isArray(raw)) return [];

                            // 1) Helper: choose a display/base key from a value + its outer key
                            const toBaseKey = (val: any, outerKey: string): string => {
                                // Prefer ddProp unless UNKNOWN; else outerKey; else ddName/xlsName; else a trimmed string
                                if (val && typeof val === "object") {
                                    if (val.ddProp && val.ddProp !== "UNKNOWN") return String(val.ddProp).trim();
                                    if (outerKey && outerKey !== "UNKNOWN") return String(outerKey).trim();
                                    if (val.ddName) return String(val.ddName).trim();
                                    if (val.xlsName) return String(val.xlsName).trim();
                                }
                                if (typeof val === "string") return val.trim();
                                return String(outerKey || "").trim();
                            };

                            // 2) Optional rename so it lines up with your dataset column names
                            //const renameMap: Record<string, string> = {
                            //    "Base Cation Exchange Capacity (M3)": "Cation Exchange Capacity",
                            //};

                            // 3) Build rows with a numeric order and original index (for stable ties)
                            type Row = { base: string; order: number; idx: number };

                            const rows: Row[] = [];
                            let idx = 0;

                            Object.entries(raw as Record<string, any>).forEach(([outerKey, val]) => {
                                const base0 = toBaseKey(val, outerKey);
                                const base = base0; //renameMap[base0] ?? base0;

                                // Prefer bbCardOrder if present; otherwise bbColOrder; both are numeric strings
                                const orderStr =
                                    (val && typeof val === "object" && (val.bbCardOrder ?? val.bbColOrder)) ?? undefined;

                                const orderNum =
                                    orderStr != null && !isNaN(Number(orderStr)) ? Number(orderStr) : Number.POSITIVE_INFINITY;

                                // Strip any accidental "_mean" suffix from the base; we append it later
                                const cleanBase = base.endsWith("_mean") ? base.slice(0, -5) : base;

                                rows.push({ base: cleanBase, order: orderNum, idx: idx++ });
                            });

                            // 4) Sort: order ascending; keep original order for ties/unordered
                            rows.sort((a, b) => (a.order === b.order ? a.idx - b.idx : a.order - b.order));
                            console.log("E082225 sorted", rows);

                            // 5) Build final list, filtering to columns that exist in columnsOn2025
                            const requestedColumns: string[] = [];
                            for (let i = 0; i < rows.length; i++) {
                                const col = `${rows[i].base}_mean`;
                                if (columnsOn2025?.[col]) requestedColumns.push(col);
                            }

                            console.log("E082225 requestedColumns", requestedColumns);
                            return requestedColumns;
                        };


                        const requestedColumns = collectRequestedKeys();
                        console.log("B082525 requestedColumns", requestedColumns);

                        const headerLabel = (meanKey: string) => {
                            const base = meanKey.endsWith("_mean") ? meanKey.slice(0, -5) : meanKey;
                            console.log("F082225 bbCardByCategory[metric]", bbCardByCategory[metric]);
                            console.log("F082225 bbCardByCategory?.[metric].?[base]", bbCardByCategory?.[metric]?.[base]);
                            return bbCardByCategory?.[metric]?.[base]?.bbColName === "" ? base : bbCardByCategory?.[metric]?.[base]?.bbColName;
                            //const base = meanKey.endsWith("_mean") ? meanKey.slice(0, -5) : meanKey;
                            //const norm = base.replace(/_/g, " ");
                        };

                        // Derived ratio columns Ca_PCT_mean / Mg_PCT_mean / K_PCT_mean
                        const computeDerivedRatio = (row: any, which: "Ca_PCT_mean" | "Mg_PCT_mean" | "K_PCT_mean") => {
                            const ca = row?.[`${getProperty(definition, ["Soluble Calcium"])?.property}_mean`] ?? 0;
                            const mg = row?.[`${getProperty(definition, ["Soluble Magnesium"])?.property}_mean`] ?? 0;
                            const k = row?.[`${getProperty(definition, ["Soluble Potassium"])?.property}_mean`] ?? 0;
                            const na = row?.[`${getProperty(definition, ["Soluble Sodium"])?.property}_mean`] ?? 0;
                            const denom = ca + mg + k + na;
                            if (!denom) return 0;
                            if (which === "Ca_PCT_mean") return ca / denom;
                            if (which === "Mg_PCT_mean") return mg / denom;
                            return k / denom;
                        };

                        // Metric-specific classes
                        const headCls = metric === "Soil Health" ? "soil-carbon-thead-th no-border" : "soil-nutrients-thead-th no-border";
                        const bodyCls = metric === "Soil Health" ? "soil-carbon-th" : "soil-nutrients-th";

                        const sorted = (dataSoilStats2025 || []).slice().sort((a: any, b: any) => (a?.top ?? 0) - (b?.top ?? 0));
                        console.log("E082225 sorted", sorted);
                        console.log("E082225 requestedColumns", requestedColumns);
                        return (
                            <div style={{ display: "inline-flex" }}>
                                <div>
                                    <table style={{ color: "#343a40 !important", textAlign: "center" }}>
                                        <thead>
                                            <tr>
                                                <th className="soil-nutrients-thead-th-label no-border"></th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {sorted.map((s: any, i: number) => (
                                                <tr key={`depth-${i}`}>
                                                    <th className="soil-nutrients-th-label no-border th-label">
                                                        {s?.top} {preferredLenAbbr}
                                                    </th>
                                                </tr>
                                            ))}
                                            <tr key="depth-bottom">
                                                <th className="soil-nutrients-th no-border th-label no-height">
                                                    {sorted.length ? sorted[sorted.length - 1]?.bottom : ""} {preferredLenAbbr}
                                                </th>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>

                                <div
                                    className="root-zone"
                                    style={{
                                        backgroundColor: "#6d757d",
                                        width: "50px",
                                        height: `${sorted.length * 50}px`,
                                        marginTop: "50px",
                                        position: "relative",
                                    }}
                                >
                                    <div className="effective-root-zone-label">
                                        Effective
                                        <br />
                                        Root
                                        <br />
                                        Zone
                                    </div>
                                    <div
                                        className="effective-root-zone-water"
                                        style={{ height: `${dataSoilDRLStats?.length ? (dataSoilDRLStats[0]?.drl_mean / 120) * 100 : 0}%` }}
                                    />
                                    <div className="root-limited-paw" style={{ height: "75%", display: "none" }} />
                                </div>

                                <div style={{ zIndex: 3, backgroundColor: "white" }}>
                                    <table style={{ color: "#343a40 !important", textAlign: "center" }}>
                                        <thead>
                                            <tr>
                                                {requestedColumns.map((col) => (
                                                    console.log("F082225 col", col),
                                                    <th key={`h-${col}`} className={headCls}>
                                                        {headerLabel(col)}
                                                    </th>
                                                ))}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {sorted.map((row: any, rIdx: number) => (
                                                <tr key={`r-${rIdx}`}>
                                                    {requestedColumns.map((col) => {
                                                        // Handle derived ratios
                                                        //console.log("082325 col", col);
                                                        if (col === "Ca_PCT_mean" || col === "Mg_PCT_mean" || col === "K_PCT_mean") {
                                                            console.log("082325 special case > col", col);
                                                            const val = computeDerivedRatio(row, col as any);
                                                            return (
                                                                <th key={`c-${rIdx}-${col}`} className={bodyCls}>
                                                                    {formatStat(val, "Soluble Sodium Percentage")}
                                                                </th>
                                                            );
                                                        }

                                                        const rowKey = toRowKey(col);
                                                        const propDisplay = (col.endsWith("_mean") ? col.slice(0, -5) : col).replace(/_/g, " ");
                                                        return (
                                                            <th key={`c-${rIdx}-${col}`} className={bodyCls}>
                                                                {formatStat(row?.[rowKey], propDisplay)}
                                                            </th>
                                                        );
                                                    })}
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                    {(() => {
                                        const rawCategory = bbCardByCategory?.[metric] || {};
                                        const items = (requestedColumns || [])
                                            .map((col, idx) => {
                                                const base = col.endsWith("_mean") ? col.slice(0, -5) : col;
                                                const cfg = rawCategory?.[base];
                                                const text = (cfg?.bbDisclaimer ?? "").toString().trim();
                                                if (!text) return null;
                                                const order = Number(cfg?.bbColOrder ?? Number.POSITIVE_INFINITY);
                                                return { text, order, idx };
                                            })
                                            .filter(Boolean) as { text: string; order: number; idx: number }[];

                                        // Deduplicate identical disclaimer lines
                                        const seen = new Set<string>();
                                        const unique = items.filter((it) => {
                                            if (seen.has(it.text)) return false;
                                            seen.add(it.text);
                                            return true;
                                        });

                                        unique.sort((a, b) => (a.order === b.order ? a.idx - b.idx : a.order - b.order));

                                        return unique.length ? (
                                            <div style={{ textAlign: "left", fontSize: "8pt" }}>
                                                {unique.map((d, i) => (
                                                    <div key={`disc-${i}`}>{d.text}</div>
                                                ))}
                                            </div>
                                        ) : null;
                                    })()}
                                </div>
                            </div>
                        );
                    };
                    const SoilStatsTable: React.FC<SoilStatsTableProps> = ({
                        metric,
                        dataSoilStats,
                        dataSoilDRLStats,
                        columnsOn,
                        definition,
                        preferredLenAbbr,
                        formatStat,
                    }) => {
                        return (
                            <div style={{ display: "inline-flex" }}>
                                <div>
                                    <table style={{ color: "#343a40 !important", textAlign: "center" }}>
                                        <thead style={{ color: "#343a40 !important" }}>
                                            <tr>
                                                <th className="soil-nutrients-thead-th-label no-border"></th>
                                                {/*<th className="soil-nutrients-thead-th no-border" ></th>*/}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {console.log("110323 dataSoilStats", dataSoilStats)}
                                            {dataSoilStats
                                                .sort(function (a: any, b: any) {
                                                    return a.top - b.top;
                                                })
                                                .map((s: any, i: number) => (
                                                    <tr key={`${i}-tr-left-nutrients`}>
                                                        <th className="soil-nutrients-th-label no-border th-label">
                                                            {s.top} {preferredLenAbbr}
                                                        </th>
                                                        {/*<th className="soil-nutrients-th" >{{depthLabels[i][0]}<br></br>}{s.KSAT_mean}</th>*/}
                                                    </tr>
                                                ))}
                                            <tr key={`x-tr-left-nutrients`}>
                                                <th className="soil-nutrients-th no-border th-label no-height">
                                                    {dataSoilStats.length > 0 ? dataSoilStats[dataSoilStats.length - 1].bottom : ""} {preferredLenAbbr}
                                                </th>
                                                {/*<th className="soil-nutrients-th no-border no-height" ></th>*/}
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                                <div
                                    className="root-zone"
                                    style={{ backgroundColor: "#6d757d", width: "50px", height: `${dataSoilStats.length * 50}px`, marginTop: "50px", position: "relative" }}
                                >
                                    <div className="effective-root-zone-label">
                                        Effective<br></br>Root<br></br>Zone
                                    </div>
                                    <div
                                        className="effective-root-zone-water"
                                        style={{ height: `${dataSoilDRLStats.length > 0 ? (dataSoilDRLStats[0].drl_mean / 120) * 100 : 0}%` }}
                                    ></div>
                                    <div className="root-limited-paw" style={{ height: "75%", display: "none" }}></div>
                                </div>
                                <div style={{ zIndex: 3, backgroundColor: "white" }}>
                                    <table style={{ color: "#343a40 !important", textAlign: "center" }}>
                                        <thead style={{ color: "#343a40 !important" }}>
                                            {metric === "Soil Fertility" && (
                                                <tr>
                                                    {columnsOn.Nitrogen_mean ? <th className="soil-nutrients-thead-th no-border">N</th> : <></>}
                                                    {columnsOn.Phosphorus_mean ? <th className="soil-nutrients-thead-th no-border">P</th> : <></>}
                                                    {columnsOn.Potassium_mean ? <th className="soil-nutrients-thead-th no-border">K</th> : <></>}
                                                    {columnsOn.Calcium_mean ? <th className="soil-nutrients-thead-th no-border">Ca</th> : <></>}
                                                    {columnsOn.Magnesium_mean ? <th className="soil-nutrients-thead-th no-border">Mg</th> : <></>}
                                                    {/*columnsOn.Sulfur_mean ? <th className="soil-nutrients-thead-th no-border" >S</th> : <></>*/}
                                                    {columnsOn.Soluble_Sulfur_mean ? <th className="soil-nutrients-thead-th no-border">S</th> : <></>}
                                                    {/*columnsOn.Organic_Matter_mean ? <th className="soil-nutrients-thead-th no-border" >OM</th> : <></>*/}
                                                    {/*columnsOn.pH_mean ? <th className="soil-nutrients-thead-th no-border" >pH</th> : <></>*/}
                                                </tr>
                                            )}
                                            {metric === "Soil Cation Ratios" && (
                                                <tr>
                                                    {columnsOn.CEC_mean ? <th className="soil-nutrients-thead-th no-border">CEC</th> : <></>}
                                                    {columnsOn.Potassium_Saturation_mean ? <th className="soil-nutrients-thead-th no-border">K-pct</th> : <></>}
                                                    {columnsOn.Magnesium_Saturation_mean ? <th className="soil-nutrients-thead-th no-border">Mg-pct</th> : <></>}
                                                    {columnsOn.Calcium_Saturation_mean ? <th className="soil-nutrients-thead-th no-border">Ca-pct</th> : <></>}
                                                    {columnsOn.Sodium_Saturation_mean ? <th className="soil-nutrients-thead-th no-border">Na-pct</th> : <></>}
                                                    {columnsOn.Base_Saturation_mean ? <th className="soil-nutrients-thead-th no-border">BS</th> : <></>}
                                                    {columnsOn.Exchangeable_Acidity_mean ? <th className="soil-nutrients-thead-th no-border">EA</th> : <></>}
                                                    {columnsOn.Exchangeable_Calcium_Percentage_mean ? <th className="soil-nutrients-thead-th no-border">Ca%</th> : <></>}
                                                    {columnsOn.Exchangeable_Magnesium_Percentage_mean ? <th className="soil-nutrients-thead-th no-border">Mg%</th> : <></>}
                                                    {columnsOn.Exchangeable_Potassium_Percentage_mean ? <th className="soil-nutrients-thead-th no-border">K%</th> : <></>}
                                                    {columnsOn.Exchangeable_Sodium_Percentage_mean ? <th className="soil-nutrients-thead-th no-border">Na%</th> : <></>}
                                                    {/*<th className="soil-nutrients-thead-th no-border" >Al-pct</th>
                                                    <th className="soil-nutrients-thead-th no-border" >NH4-pct</th>*/}
                                                </tr>
                                            )}
                                            {metric === "Soil Micronutrients" && (
                                                <tr>
                                                    {columnsOn.Zinc_mean ? <th className="soil-nutrients-thead-th no-border">Zn</th> : <></>}
                                                    {columnsOn.Iron_mean ? <th className="soil-nutrients-thead-th no-border">Fe</th> : <></>}
                                                    {columnsOn.Manganese_mean ? <th className="soil-nutrients-thead-th no-border">Mn</th> : <></>}
                                                    {columnsOn.Copper_mean ? <th className="soil-nutrients-thead-th no-border">Cu</th> : <></>}
                                                    {columnsOn.Soluble_Boron_mean ? <th className="soil-nutrients-thead-th no-border">B</th> : <></>}
                                                    {/*columnsOn.Boron_mean ? <th className="soil-nutrients-thead-th no-border" >B</th> : <></>*/}
                                                    {/*columnsOn.Sulfur_mean ? <th className="soil-nutrients-thead-th no-border" >S</th> : <></>*/}
                                                </tr>
                                            )}
                                            {metric === "Soil Health" && (
                                                <tr>
                                                    {columnsOn.Organic_Matter_mean ? <th className="soil-carbon-thead-th no-border">OM</th> : <></>}
                                                    {/*columnsOn.Organic_Matter_mean ? <th className="soil-carbon-thead-th no-border" >SOC</th> : <></>*/}
                                                    {columnsOn.BD_mean ? <th className="soil-carbon-thead-th no-border">BD</th> : <></>}
                                                    {columnsOn.Macroporosity_mean ? <th className="soil-carbon-thead-th no-border">MP</th> : <></>}
                                                    {columnsOn.Macropore_Connectivity_mean ? <th className="soil-carbon-thead-th no-border">MPC</th> : <></>}
                                                </tr>
                                            )}
                                            {metric === "Soil Paste" && (
                                                <tr>
                                                    {columnsOn.Saturation_Percentage_mean ? <th className="soil-nutrients-thead-th no-border">SP</th> : <></>}
                                                    {columnsOn.Electrical_Conductivity_of_Saturated_Paste_Extract_mean ? (
                                                        <th className="soil-nutrients-thead-th no-border">EC</th>
                                                    ) : (
                                                        <></>
                                                    )}
                                                    {columnsOn.pH_mean ? <th className="soil-nutrients-thead-th no-border">pH</th> : <></>}
                                                    {columnsOn.Soluble_Calcium_mean ? <th className="soil-nutrients-thead-th no-border">Ca</th> : <></>}
                                                    {columnsOn.Soluble_Magnesium_mean ? <th className="soil-nutrients-thead-th no-border">Mg</th> : <></>}
                                                    {columnsOn.Soluble_Potassium_mean ? <th className="soil-nutrients-thead-th no-border">K</th> : <></>}
                                                    {columnsOn.Soluble_Sodium_mean ? <th className="soil-nutrients-thead-th no-border">Na</th> : <></>}
                                                    {columnsOn.Soluble_Salts_mean ? <th className="soil-nutrients-thead-th no-border">salts</th> : <></>}
                                                </tr>
                                            )}
                                            {metric === "Soil Paste Ratios" && (
                                                <tr>
                                                    {columnsOn.Soluble_Sodium_Percentage_mean ? <th className="soil-nutrients-thead-th no-border">Na%</th> : <></>}
                                                    {columnsOn.Sodium_Adsorption_Ratio_mean ? <th className="soil-nutrients-thead-th no-border">SAR</th> : <></>}
                                                    {columnsOn.Ca_PCT_mean ? <th className="soil-nutrients-thead-th no-border">Ca%</th> : <></>}
                                                    {columnsOn.Mg_PCT_mean ? <th className="soil-nutrients-thead-th no-border">Mg%</th> : <></>}
                                                    {columnsOn.K_PCT_mean ? <th className="soil-nutrients-thead-th no-border">K%</th> : <></>}
                                                </tr>
                                            )}
                                        </thead>
                                        <tbody>
                                            {metric === "Soil Fertility" &&
                                                dataSoilStats.map((s: any, i: number) => (
                                                    <tr key={`${i}-tr-nutrients`}>
                                                        {columnsOn.Nitrogen_mean ? (
                                                            <th key={`${i}-th-nutrients-n`} className="soil-nutrients-th">
                                                                {formatStat(s.Nitrogen_mean, "Nitrogen")}
                                                            </th>
                                                        ) : (
                                                            <></>
                                                        )}
                                                        {columnsOn.Phosphorus_mean ? (
                                                            <th key={`${i}-th-nutrients-p`} className="soil-nutrients-th">
                                                                {formatStat(s.Phosphorus_mean, "Phosphorus")}
                                                            </th>
                                                        ) : (
                                                            <></>
                                                        )}
                                                        {columnsOn.Potassium_mean ? (
                                                            <th key={`${i}-th-nutrients-k`} className="soil-nutrients-th">
                                                                {formatStat(s.Potassium_mean, "Potassium")}
                                                            </th>
                                                        ) : (
                                                            <></>
                                                        )}
                                                        {columnsOn.Calcium_mean ? (
                                                            <th key={`${i}-th-nutrients-c`} className="soil-nutrients-th">
                                                                {formatStat(s.Calcium_mean, "Calcium")}
                                                            </th>
                                                        ) : (
                                                            <></>
                                                        )}
                                                        {columnsOn.Magnesium_mean ? (
                                                            <th key={`${i}-th-nutrients-m`} className="soil-nutrients-th">
                                                                {formatStat(s.Magnesium_mean, "Magnesium")}
                                                            </th>
                                                        ) : (
                                                            <></>
                                                        )}
                                                        {columnsOn.Soluble_Sulfur_mean ? (
                                                            <th key={`${i}-th-nutrients-s`} className="soil-nutrients-th">
                                                                {formatStat(s["Soluble Sulfur_mean"], "Soluble Sulfur")}
                                                            </th>
                                                        ) : (
                                                            <></>
                                                        )}
                                                        {/*columnsOn.Sulfur_mean ? <th key={`${i}-th-nutrients-s`} className="soil-nutrients-th" >{formatStat(s["Sulfur_mean"], "Sulfur")}</th> : <></>*/}
                                                        {/*columnsOn.Organic_Matter_mean ? <th key={`${i}-th-nutrients-sm`} className="soil-nutrients-th" >{formatStat(s["Organic Matter_mean"], "Organic Matter")}</th> : <></>*/}
                                                        {/*columnsOn.pH_mean ? <th key={`${i}-th-nutrients-ph`} className="soil-nutrients-th" >{formatStat(s.pH_mean, "pH")}</th> : <></>*/}
                                                    </tr>
                                                ))}
                                            {metric === "Soil Cation Ratios" &&
                                                dataSoilStats.map((s: any, i: number) => (
                                                    <tr key={`${i}-tr-nutrients`}>
                                                        {columnsOn.CEC_mean ? (
                                                            <th key={`${i}-th-nutrients-cec`} className="soil-nutrients-th">
                                                                {formatStat(s.CEC_mean, "CEC")}
                                                            </th>
                                                        ) : (
                                                            <></>
                                                        )}
                                                        {columnsOn.Calcium_Saturation_mean ? (
                                                            <th key={`${i}-th-nutrients-ca`} className="soil-nutrients-th">
                                                                {formatStat(s["Calcium Saturation_mean"], "Calcium Saturation")}
                                                            </th>
                                                        ) : (
                                                            <></>
                                                        )}
                                                        {columnsOn.Magnesium_Saturation_mean ? (
                                                            <th key={`${i}-th-nutrients-m2`} className="soil-nutrients-th">
                                                                {formatStat(s["Magnesium Saturation_mean"], "Magnesium Saturation")}
                                                            </th>
                                                        ) : (
                                                            <></>
                                                        )}
                                                        {columnsOn.Potassium_Saturation_mean ? (
                                                            <th key={`${i}-th-nutrients-k2`} className="soil-nutrients-th">
                                                                {formatStat(s["Potassium Saturation_mean"], "Potassium Saturation")}
                                                            </th>
                                                        ) : (
                                                            <></>
                                                        )}
                                                        {columnsOn.Sodium_Saturation_mean ? (
                                                            <th key={`${i}-th-nutrients-na`} className="soil-nutrients-th">
                                                                {formatStat(s["Sodium Saturation_mean"], "Sodium Saturation")}
                                                            </th>
                                                        ) : (
                                                            <></>
                                                        )}
                                                        {columnsOn.Base_Saturation_mean ? (
                                                            <th key={`${i}-th-nutrients-bs1`} className="soil-nutrients-th">
                                                                {formatStat(s["Base Saturation_mean"], "Base Saturation")}
                                                            </th>
                                                        ) : (
                                                            <></>
                                                        )}
                                                        {columnsOn.Exchangeable_Acidity_mean ? (
                                                            <th key={`${i}-th-nutrients-ea1`} className="soil-nutrients-th">
                                                                {formatStat(s["Exchangeable Acidity_mean"], "Exchangeable Acidity")}
                                                            </th>
                                                        ) : (
                                                            <></>
                                                        )}
                                                        {columnsOn.Exchangeable_Calcium_Percentage_mean ? (
                                                            <th key={`${i}-th-nutrients-ea1`} className="soil-nutrients-th">
                                                                {formatStat(s["Exchangeable Calcium Percentage_mean"], "Exchangeable Calcium Percentage")}
                                                            </th>
                                                        ) : (
                                                            <></>
                                                        )}
                                                        {columnsOn.Exchangeable_Magnesium_Percentage_mean ? (
                                                            <th key={`${i}-th-nutrients-ea1`} className="soil-nutrients-th">
                                                                {formatStat(s["Exchangeable Magnesium Percentage_mean"], "Exchangeable Magnesium Percentage")}
                                                            </th>
                                                        ) : (
                                                            <></>
                                                        )}
                                                        {columnsOn.Exchangeable_Potassium_Percentage_mean ? (
                                                            <th key={`${i}-th-nutrients-ea1`} className="soil-nutrients-th">
                                                                {formatStat(s["Exchangeable Potassium Percentage_mean"], "Exchangeable Potassium Percentage")}
                                                            </th>
                                                        ) : (
                                                            <></>
                                                        )}
                                                        {columnsOn.Exchangeable_Sodium_Percentage_mean ? (
                                                            <th key={`${i}-th-nutrients-ea1`} className="soil-nutrients-th">
                                                                {formatStat(s["Exchangeable Sodium Percentage_mean"], "Exchangeable Sodium Percentage")}
                                                            </th>
                                                        ) : (
                                                            <></>
                                                        )}
                                                        {/*columnsOn.Hydrogen_Saturation_mean ? <th key={`${i}-th-nutrients-h`} className="soil-nutrients-th" >{formatStat(s["Hydrogen Saturation_mean"], "Hydrogen Saturation")}</th> : <></>*/}
                                                        {/*<th key={`${i}-th-nutrients-al`} className="soil-nutrients-th" >{formatStat(s["Aluminum_mean"], "Aluminum")}</th>
                                                    <th key={`${i}-th-nutrients-nh4`} className="soil-nutrients-th" >{formatStat(s["Ammonium_mean"], "Amonium")}</th>*/}
                                                    </tr>
                                                ))}
                                            {metric === "Soil Micronutrients" &&
                                                dataSoilStats.map((s: any, i: number) => (
                                                    <tr key={`${i}-tr-nutrients`}>
                                                        {columnsOn.Zinc_mean ? (
                                                            <th key={`${i}-th-nutrients-zn`} className="soil-nutrients-th">
                                                                {formatStat(s["Zinc_mean"], "Zinc")}
                                                            </th>
                                                        ) : (
                                                            <></>
                                                        )}
                                                        {columnsOn.Iron_mean ? (
                                                            <th key={`${i}-th-nutrients-fe`} className="soil-nutrients-th">
                                                                {formatStat(s["Iron_mean"], "Iron")}
                                                            </th>
                                                        ) : (
                                                            <></>
                                                        )}
                                                        {columnsOn.Manganese_mean ? (
                                                            <th key={`${i}-th-nutrients-mn`} className="soil-nutrients-th">
                                                                {formatStat(s["Manganese_mean"], "Manganese")}
                                                            </th>
                                                        ) : (
                                                            <></>
                                                        )}
                                                        {columnsOn.Copper_mean ? (
                                                            <th key={`${i}-th-nutrients-cu`} className="soil-nutrients-th">
                                                                {formatStat(s["Copper_mean"], "Copper")}
                                                            </th>
                                                        ) : (
                                                            <></>
                                                        )}
                                                        {columnsOn.Soluble_Boron_mean ? (
                                                            <th key={`${i}-th-nutrients-b`} className="soil-nutrients-th">
                                                                {formatStat(s["Soluble Boron_mean"], "Soluble Boron")}
                                                            </th>
                                                        ) : (
                                                            <></>
                                                        )}
                                                        {/*columnsOn.Boron_mean ? <th key={`${i}-th-nutrients-b`} className="soil-nutrients-th" >{formatStat(s["Boron_mean"], "Boron")}</th> : <></>*/}
                                                        {/*columnsOn.Sulfur_mean ? <th key={`${i}-th-nutrients-ph`} className="soil-nutrients-th" >{formatStat(s["Sulfur_mean"], "Sulfur")}</th> : <></>*/}
                                                    </tr>
                                                ))}
                                            {metric === "Soil Health" &&
                                                dataSoilStats.map((s: any, i: number) => (
                                                    (console.log("011224 s", s),
                                                    <tr key={`${i}-tr-nutrients`}>
                                                        {columnsOn.Organic_Matter_mean ? (
                                                            <th key={`${i}-th-carbon-om`} className="soil-carbon-th">
                                                                {formatStat(s["Organic Matter_mean"], "Organic Matter")}
                                                            </th>
                                                        ) : (
                                                            <></>
                                                        )}
                                                        {/*columnsOn.Organic_Matter_mean ? <th key={`${i}-th-carbon-om`} className="soil-carbon-th" >{formatStat(s["Organic Matter_mean"] / 1.72, "Organic Matter")}</th> : <></>*/}
                                                        {columnsOn.BD_mean ? (
                                                            <th key={`${i}-th-carbon-bd`} className="soil-carbon-th">
                                                                {formatStat(s["BD_mean"], "BD")}
                                                            </th>
                                                        ) : (
                                                            <></>
                                                        )}
                                                        {columnsOn.Macroporosity_mean ? (
                                                            <th key={`${i}-th-carbon-mp`} className="soil-carbon-th">
                                                                {formatStat(s["Macroporosity_mean"], "Macroporosity")}
                                                            </th>
                                                        ) : (
                                                            <></>
                                                        )}
                                                        {columnsOn.Macropore_Connectivity_mean ? (
                                                            <th key={`${i}-th-carbon-mpc`} className="soil-carbon-th">
                                                                {formatStat(s["Macropore Connectivity_mean"], "Macropore Connectivity")}
                                                            </th>
                                                        ) : (
                                                            <></>
                                                        )}
                                                    </tr>)
                                                ))}
                                            {
                                                // SP == Saturation Percentage == Saturation Percentage_mean > 36.2622
                                                // EC == Electrical Conductivity of Saturated Paste Extract == Electrical Conductivity of Saturated Paste Extract_mean > 0.2377
                                                // Ca - sol == Soluble Calcium == Soluble Calcium_mean > 0.3065
                                                // Mg - sol == Soluble Magnesium == Soluble Magnesium_mean > 0.0006
                                                // K - sol == Soluble Potassium == Soluble Potassium_mean > 0.2519
                                                // Na - sol == Soluble Sodium == Soluble Sodium_mean > 0.0031
                                                // Salts == Soluble Salts == Soluble Salts_mean > 0.5621
                                                metric === "Soil Paste" &&
                                                    dataSoilStats.map((s: any, i: number) => (
                                                        (console.log("011224 s", s),
                                                        <tr key={`${i}-tr-nutrients`}>
                                                            {columnsOn.Saturation_Percentage_mean ? (
                                                                <th key={`${i}-th-nutrients-sp`} className="soil-nutrients-th">
                                                                    {formatStat(s["Saturation Percentage_mean"], "Saturation Percentage")}
                                                                </th>
                                                            ) : (
                                                                <></>
                                                            )}
                                                            {columnsOn.Electrical_Conductivity_of_Saturated_Paste_Extract_mean ? (
                                                                <th key={`${i}-th-nutrients-ecspe`} className="soil-nutrients-th">
                                                                    {formatStat(
                                                                        s["Electrical Conductivity of Saturated Paste Extract_mean"],
                                                                        "Electrical Conductivity of Saturated Paste Extract"
                                                                    )}
                                                                </th>
                                                            ) : (
                                                                <></>
                                                            )}
                                                            {columnsOn.pH_mean ? (
                                                                <th key={`${i}-th-nutrients-ph3`} className="soil-nutrients-th">
                                                                    {formatStat(s.pH_mean, "pH")}
                                                                </th>
                                                            ) : (
                                                                <></>
                                                            )}
                                                            {columnsOn.Soluble_Calcium_mean ? (
                                                                <th key={`${i}-th-nutrients-sc`} className="soil-nutrients-th">
                                                                    {formatStat(s["Soluble Calcium_mean"], "Soluble Calcium")}
                                                                </th>
                                                            ) : (
                                                                <></>
                                                            )}
                                                            {columnsOn.Soluble_Magnesium_mean ? (
                                                                <th key={`${i}-th-nutrients-sm`} className="soil-nutrients-th">
                                                                    {formatStat(s["Soluble Magnesium_mean"], "Soluble Magnesium")}
                                                                </th>
                                                            ) : (
                                                                <></>
                                                            )}
                                                            {columnsOn.Soluble_Potassium_mean ? (
                                                                <th key={`${i}-th-nutrients-sk`} className="soil-nutrients-th">
                                                                    {formatStat(s["Soluble Potassium_mean"], "Soluble Potassium")}
                                                                </th>
                                                            ) : (
                                                                <></>
                                                            )}
                                                            {columnsOn.Soluble_Sodium_mean ? (
                                                                <th key={`${i}-th-nutrients-ss`} className="soil-nutrients-th">
                                                                    {formatStat(s["Soluble Sodium_mean"], "Soluble Sodium")}
                                                                </th>
                                                            ) : (
                                                                <></>
                                                            )}
                                                            {columnsOn.Soluble_Salts_mean ? (
                                                                <th key={`${i}-th-nutrients-sst`} className="soil-nutrients-th">
                                                                    {formatStat(s["Soluble Salts_mean"], "Soluble Salts")}
                                                                </th>
                                                            ) : (
                                                                <></>
                                                            )}
                                                        </tr>)
                                                    ))
                                            }
                                            {
                                                // Soil Paste Ratios
                                                // X Sol Ca - pct == NG??? Soluble Calcium | Soluble Calcium Magnesium Ratio
                                                // X Sol Mg - pct == NG??? Soluble Magnesium
                                                // X Sol K - pct == NG??? Soluble Potassium | Soluble Potassium Magnesium Ratio
                                                // Sol Na - pct == Soluble Sodium Percentage == Soluble Sodium Percentage_mean > 13.7681
                                                // SAR == Sodium Adsorption Ratio == Sodium Adsorption Ratio_mean > 0.5133
                                                metric === "Soil Paste Ratios" &&
                                                    dataSoilStats.map((s: any, i: number) => {
                                                        const caSol = s[`${getProperty(definition, ["Soluble Calcium"])?.property}_mean`] || 0;
                                                        const mgSol = s[`${getProperty(definition, ["Soluble Magnesium"])?.property}_mean`] || 0;
                                                        const kSol = s[`${getProperty(definition, ["Soluble Potassium"])?.property}_mean`] || 0;
                                                        const naSol = s[`${getProperty(definition, ["Soluble Sodium"])?.property}_mean`] || 0;

                                                        const denom = caSol + mgSol + kSol + naSol;
                                                        const Ca_PCT_mean = denom ? caSol / denom : 0;
                                                        const Mg_PCT_mean = denom ? mgSol / denom : 0;
                                                        const K_PCT_mean = denom ? kSol / denom : 0;

                                                        console.log("011224 s", s);
                                                        return (
                                                            <tr key={`${i}-tr-nutrients`}>
                                                                {columnsOn.Soluble_Sodium_Percentage_mean ? (
                                                                    <th key={`${i}-th-nutrients-ssp`} className="soil-nutrients-th">
                                                                        {formatStat(s["Soluble Sodium Percentage_mean"], "Soluble Sodium Percentage")}
                                                                    </th>
                                                                ) : (
                                                                    <></>
                                                                )}
                                                                {columnsOn.Sodium_Adsorption_Ratio_mean ? (
                                                                    <th key={`${i}-th-nutrients-sar`} className="soil-nutrients-th">
                                                                        {formatStat(s["Sodium Adsorption Ratio_mean"], "Sodium Adsorption Ratio")}
                                                                    </th>
                                                                ) : (
                                                                    <></>
                                                                )}
                                                                {columnsOn.Ca_PCT_mean ? (
                                                                    <th key={`${i}-th-nutrients-sar`} className="soil-nutrients-th">
                                                                        {formatStat(Ca_PCT_mean, "Soluble Sodium Percentage")}
                                                                    </th>
                                                                ) : (
                                                                    <></>
                                                                )}
                                                                {columnsOn.Mg_PCT_mean ? (
                                                                    <th key={`${i}-th-nutrients-sar`} className="soil-nutrients-th">
                                                                        {formatStat(Mg_PCT_mean, "Soluble Sodium Percentage")}
                                                                    </th>
                                                                ) : (
                                                                    <></>
                                                                )}
                                                                {columnsOn.K_PCT_mean ? (
                                                                    <th key={`${i}-th-nutrients-sar`} className="soil-nutrients-th">
                                                                        {formatStat(K_PCT_mean, "Soluble Sodium Percentage")}
                                                                    </th>
                                                                ) : (
                                                                    <></>
                                                                )}
                                                            </tr>
                                                        );
                                                    })
                                            }
                                        </tbody>
                                    </table>
                                    {metric === "Soil Cation Ratios" && <div style={{ textAlign: "center", fontSize: "8pt" }}>Ammonium acetate extractable cations.</div>}
                                    {metric === "Soil Paste" && <div style={{ textAlign: "center", fontSize: "8pt" }}>Water soluble cations from saturated paste extract.</div>}
                                    {metric === "Soil Health" && (
                                        <div style={{ textAlign: "center", fontSize: "8pt" }}>
                                            MP = Macroporosity<br></br>
                                            MPC = Macropore Connectivity
                                        </div>
                                    )}
                                </div>
                            </div>
                        );
                    };

                    return (<>
                            <SoilStatsTable2025
                                metric={metric}
                                dataSoilStats2025={dataSoilStats2025}
                                dataSoilDRLStats={dataSoilDRLStats}
                                columnsOn2025={columnsOn2025}
                                definition={definition}
                                preferredLenAbbr={preferredLenAbbr}
                                formatStat={formatStat}
                                bbCardByCategory={bbCardByCategory}
                            />
                            {/*<SoilStatsTable
                                metric={metric}
                                dataSoilStats={dataSoilStats}
                                dataSoilDRLStats={dataSoilDRLStats}
                                columnsOn={columnsOn}
                                definition={definition}
                                preferredLenAbbr={preferredLenAbbr}
                                formatStat={formatStat}
                            />*/}
                        </>
                    );
                })()}
                </>
                </>
                }
            </dd>
        </div>
    );
}

export default AboveWidgetSoilFertilityCell;